import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { Arrow } from "@components/icons"
import TweenLite, { Power4 } from "gsap/TweenLite"
import _ from "lodash-es"

const ScrollContainer = styled.div`
  align-items: center;
  justify-content: flex-start;
  flex-direction: "row";
  display: flex;
`

const ScrollTrack = styled.div`
  height: 1px;
  width: 100%;
  background: #000000;
  opacity: 0.1;
`

const ScrollBar = styled.div`
  position: absolute;
  height: 1px;
  background: #000000;
  will-change: transform;
`

export const ScrollButtons = styled.div<{ showControlsOnMobile: boolean }>`
  display: ${({ showControlsOnMobile }) =>
    showControlsOnMobile ? "flex" : "none"};
  flex-direction: "row";
  align-items: center;
  justify-content: flex-start;
  margin-left: 20px;

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const ScrollButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  margin: 0;
  padding: 0 20px;

  &:focus {
    outline: none;
  }
`

const LeftArrow = styled.div`
  transform: rotate(180deg);
  height: 24px;
  width: 24px;
`

const RightArrow = styled.div`
  height: 24px;
  width: 24px;
`

interface ScrollbarProps {
  containerRef: React.RefObject<HTMLDivElement>
  className?: string
  showControlsOnMobile?: boolean
}

interface ScrollbarState {
  showScrollbar: boolean
  translateX: number
  barWidth: number
}

class Scrollbar extends React.PureComponent<ScrollbarProps, ScrollbarState> {
  state: ScrollbarState = {
    showScrollbar: false,
    translateX: 0,
    barWidth: 0,
  }
  barRef = React.createRef<HTMLDivElement>()
  trackRef = React.createRef<HTMLDivElement>()

  calculateScroll = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    const track = this.trackRef.current
    const bar = this.barRef.current
    if (container && track && bar) {
      // Don't show the scrollbar if there is no overflows
      const showScrollbar = container.scrollWidth > container.clientWidth
      if (!showScrollbar) {
        return this.setState({ showScrollbar })
      }

      // Calcuate the current scroll percentage (0 to 1)
      const scrollPercentage =
        1 -
        (container.scrollWidth - container.scrollLeft - container.clientWidth) /
          (container.scrollWidth - container.clientWidth)

      // Calculate the bar width
      const trackWidth = track.clientWidth
      const barWidth =
        trackWidth * (container.clientWidth / container.scrollWidth)

      // Calculate the translation of the bar
      const widthDiff = trackWidth - barWidth
      const translateX = widthDiff * scrollPercentage

      // Set the styling of the bar
      bar.style.width = `calc(${barWidth}px)`
      bar.style.transform = `translateX(${translateX}px)`
      this.setState({ showScrollbar })
    }
  }

  onPrev = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft - container.clientWidth,
        0,
        container.scrollWidth - container.clientWidth
      )
      TweenLite.to(container, 0.4, { scrollLeft: left, ease: Power4.easeOut })
    }
  }

  onNext = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft + container.clientWidth,
        0,
        container.scrollWidth - container.clientWidth
      )
      TweenLite.to(container, 0.4, { scrollLeft: left, ease: Power4.easeOut })
    }
  }

  componentDidMount() {
    const { containerRef } = this.props
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", this.calculateScroll)
      window.addEventListener("resize", this.calculateScroll)
    }
    this.calculateScroll()
  }

  componentWillUnmount() {
    const { containerRef } = this.props
    if (containerRef.current) {
      containerRef.current.removeEventListener("scroll", this.calculateScroll)
      window.removeEventListener("resize", this.calculateScroll)
    }
  }

  render() {
    const { className } = this.props
    return (
      <ScrollContainer className={className}>
        <ScrollTrack ref={this.trackRef} />
        <ScrollBar ref={this.barRef} />
        <ScrollButtons showControlsOnMobile={!!this.props.showControlsOnMobile}>
          <ScrollButton onClick={this.onPrev} aria-label="Scroll Left">
            <LeftArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </LeftArrow>
          </ScrollButton>
          <ScrollButton onClick={this.onNext} aria-label="Scroll Right">
            <RightArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </RightArrow>
          </ScrollButton>
        </ScrollButtons>
      </ScrollContainer>
    )
  }
}

export default Scrollbar
